import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { InputAdornment, TextField } from "@mui/material";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import WebRoundedIcon from "@mui/icons-material/WebRounded";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { Link, useSearchParams } from "react-router-dom";
import { selectUser } from "../../../features/userSlice";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { EmptyState, LoadingState, StatTile } from "../../../components/UI/States";
import { fetchAccessOverview, fetchAccessUsers } from "./accessApi";
import { buildPageCatalog, buildRoleCatalog } from "./accessCatalog";
import { Notice, RoleChip, TabBar, UserAvatar } from "./accessUi";
import PageAccessDrawer from "./PageAccessDrawer";
import RolesPanel from "./RolesPanel";
import AuditList from "./AuditList";

const BASE_SHOWN = 3;
const PEOPLE_SHOWN = 3;

// Admin: let extra roles or specific people open role-gated pages, and
// create custom roles. Grants are additive — a page's code roles always
// apply — and reach people within ~20 seconds (utils/liveSession.js).
const PageAccess = () => {
  const user = useSelector(selectUser);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ?tab=roles from the User Manager's Roles button.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() =>
    ["pages", "roles", "changes"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "pages"
  );
  const [query, setQuery] = useState("");
  const [onlyExtra, setOnlyExtra] = useState(false);
  const [openKey, setOpenKey] = useState(null);

  const pages = useMemo(() => buildPageCatalog(), []);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [nextOverview, nextUsers] = await Promise.all([
        fetchAccessOverview(user.id),
        fetchAccessUsers(user.id),
      ]);
      setOverview(nextOverview);
      setUsers(nextUsers.users || []);
    } catch (err) {
      setError(err.message || "Couldn't load page access.");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Quiet refresh after a change: counts and the change log catch up.
  const refreshOverview = useCallback(async () => {
    try {
      setOverview(await fetchAccessOverview(user.id));
    } catch (err) {
      // The change itself already saved; the next manual refresh catches up.
    }
  }, [user?.id]);

  const grantsByKey = useMemo(
    () => new Map((overview?.grants || []).map((g) => [g.key, g])),
    [overview]
  );
  const usersById = useMemo(
    () => new Map(users.filter((u) => u.id).map((u) => [u.id, u])),
    [users]
  );
  const roleCatalog = useMemo(
    () => buildRoleCatalog({ customRoles: overview?.customRoles, users, pages }),
    [overview, users, pages]
  );

  const handleGrantChange = (grant) => {
    setOverview((prev) => {
      const rest = (prev?.grants || []).filter((g) => g.key !== grant.key);
      const empty = !grant.roles.length && !grant.users.length;
      return { ...prev, grants: empty ? rest : [...rest, grant] };
    });
    refreshOverview();
  };

  const extraCount = pages.filter((p) => grantsByKey.has(p.key)).length;
  const directPeople = new Set((overview?.grants || []).flatMap((g) => g.users)).size;

  const visiblePages = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pages.filter((page) => {
      const grant = grantsByKey.get(page.key);
      if (onlyExtra && !grant) return false;
      if (!q) return true;
      const names = (grant?.users || []).map((id) => usersById.get(id)?.name || "");
      return [page.name, page.section, page.key, ...page.roles, ...(grant?.roles || []), ...names]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [pages, grantsByKey, usersById, query, onlyExtra]);

  const sections = useMemo(() => {
    const map = new Map();
    visiblePages.forEach((page) => {
      if (!map.has(page.section)) map.set(page.section, []);
      map.get(page.section).push(page);
    });
    return [...map.entries()];
  }, [visiblePages]);

  const openPage = pages.find((p) => p.key === openKey);

  return (
    <PageShell size="2xl">
      <PageHeader
        icon={KeyRoundedIcon}
        tone="violet"
        eyebrow="Admin"
        eyebrowTo="/admin"
        title="Page Access"
        description="Let extra roles or specific people open a page, and create your own roles. Changes reach people within about 20 seconds — nobody has to sign out."
        actions={
          <>
            <Link to="/admin/user/userManager" className="app-btn app-btn-secondary">
              <ManageAccountsIcon sx={{ fontSize: 18 }} />
              User Manager
            </Link>
            <button type="button" onClick={load} disabled={loading} className="app-btn app-btn-secondary">
              <RefreshIcon sx={{ fontSize: 18 }} />
              Refresh
            </button>
          </>
        }
      />

      {error && <Notice message={{ type: "error", text: error }} className="mb-4" />}

      {loading && !overview ? (
        <LoadingState label="Loading page access…" />
      ) : overview ? (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile icon={WebRoundedIcon} label="Role-gated pages" value={pages.length} />
            <StatTile
              icon={LockOpenRoundedIcon}
              label="With extra access"
              value={extraCount}
              tone="sky"
              active={onlyExtra && tab === "pages"}
              onClick={() => {
                setTab("pages");
                setOnlyExtra((v) => !v);
              }}
            />
            <StatTile
              icon={BadgeRoundedIcon}
              label="Custom roles"
              value={overview.customRoles.length}
              tone="violet"
              onClick={() => setTab("roles")}
            />
            <StatTile
              icon={GroupRoundedIcon}
              label="People with direct access"
              value={directPeople}
              tone="emerald"
            />
          </div>

          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <TabBar
              label="Page Access sections"
              value={tab}
              onChange={setTab}
              tabs={[
                { value: "pages", label: "Pages", count: pages.length },
                { value: "roles", label: "Roles", count: roleCatalog.length },
                { value: "changes", label: "Recent changes" },
              ]}
            />
            {tab === "pages" && (
              <div className="flex flex-wrap items-center gap-2">
                <label className="app-chip cursor-pointer select-none py-1.5">
                  <input
                    type="checkbox"
                    checked={onlyExtra}
                    onChange={(e) => setOnlyExtra(e.target.checked)}
                    className="mr-1 accent-sky-600"
                  />
                  Only with extra access
                </label>
                <TextField
                  size="small"
                  placeholder="Search pages, roles, people…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="min-w-0 flex-1 sm:w-72 sm:flex-none"
                  inputProps={{ "aria-label": "Search pages" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            )}
          </div>

          {tab === "pages" &&
            (sections.length === 0 ? (
              <div className="app-card rounded-2xl border">
                <EmptyState
                  compact
                  icon={LockOpenRoundedIcon}
                  title={onlyExtra ? "No pages have extra access yet" : "No pages match"}
                  description={onlyExtra ? "Open any page below to add a role or a person." : "Try a different search."}
                />
              </div>
            ) : (
              <div className="space-y-5">
                {sections.map(([section, sectionPages]) => (
                  <section key={section} className="app-card overflow-hidden rounded-2xl border">
                    <p className="app-eyebrow app-divider border-b px-4 py-2.5 sm:px-5">{section}</p>
                    <ul className="divide-y divide-[color:var(--app-divider)]">
                      {sectionPages.map((page) => {
                        const grant = grantsByKey.get(page.key);
                        const people = (grant?.users || []).map(
                          (id) => usersById.get(id) || { id, name: "Unknown user" }
                        );
                        return (
                          <li key={page.key}>
                            <button
                              type="button"
                              onClick={() => setOpenKey(page.key)}
                              className="flex w-full flex-col gap-2 px-4 py-3 text-left transition hover:bg-[color:var(--app-hover)] sm:flex-row sm:items-center sm:gap-4 sm:px-5"
                            >
                              <div className="min-w-0 sm:w-64 sm:shrink-0">
                                <p className="truncate font-medium">{page.name}</p>
                                <p className="app-muted truncate font-mono text-xs">
                                  {page.redirect || page.paths[0]}
                                </p>
                              </div>
                              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                                {page.roles.slice(0, BASE_SHOWN).map((role) => (
                                  <RoleChip key={role} role={role} tone="base" />
                                ))}
                                {page.roles.length > BASE_SHOWN && (
                                  <span className="app-muted text-xs">+{page.roles.length - BASE_SHOWN}</span>
                                )}
                                {(grant?.roles || []).map((role) => (
                                  <RoleChip key={`x-${role}`} role={role} title="Added on Page Access" />
                                ))}
                                {people.length > 0 && (
                                  <span className="ml-1 inline-flex items-center gap-1.5">
                                    <span className="flex -space-x-2">
                                      {people.slice(0, PEOPLE_SHOWN).map((p) => (
                                        <UserAvatar key={p.id} user={p} size="sm" />
                                      ))}
                                    </span>
                                    <span className="text-xs font-medium text-sky-700 dark:text-sky-300">
                                      {people.length === 1
                                        ? people[0].name
                                        : `${people.length} people`}
                                    </span>
                                  </span>
                                )}
                              </div>
                              <ChevronRightRoundedIcon className="app-muted hidden shrink-0 sm:block" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ))}
              </div>
            ))}

          {tab === "roles" && (
            <RolesPanel
              roleCatalog={roleCatalog}
              roleCounts={overview.roleCounts || {}}
              pages={pages}
              grantsByKey={grantsByKey}
              users={users}
              actorId={user?.id}
              onChanged={refreshOverview}
              onOpenPage={setOpenKey}
            />
          )}

          {tab === "changes" && (
            <div className="app-card rounded-2xl border px-4 sm:px-5">
              <AuditList entries={overview.audit} emptyText="No access changes yet." />
            </div>
          )}
        </>
      ) : null}

      {openPage && (
        <PageAccessDrawer
          key={openPage.key}
          page={openPage}
          grant={grantsByKey.get(openPage.key)}
          users={users}
          roleCatalog={roleCatalog}
          actorId={user?.id}
          onGrantChange={handleGrantChange}
          onClose={() => setOpenKey(null)}
        />
      )}
    </PageShell>
  );
};

export default PageAccess;
